import React, { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { ProgressStep } from '@monorepo/types';
import { DebugPanel, JSProgressLines, ProgressNode } from './components';
import { useActiveStepScroll, useDragScroll } from './hooks';
import { ProgressContainer, StepsContainer } from './styled';
import {
  calculateProgressPercentage,
  findActiveStepIndex,
  NodeState,
  useStepSpacing,
} from './utils';

export interface CustomProgressBarProps {
  steps: ProgressStep[];
  currentUserProgress?: number; // Current user's progress amount (PNL)
  onDebugStepClick?: (stepId: string) => void; // Debug function to change active step
  hasError?: boolean; // Flag for error state (terminated/suspended/expired)
  showDebug?: boolean; // Flag to show/hide debug panel (default: false for production)
  groupInfo?: { groupId: string; groupName: string }; // Group information
}

export const CustomProgressBar: FC<CustomProgressBarProps> = ({
  steps,
  currentUserProgress = 0,
  onDebugStepClick,
  hasError = false,
  showDebug = false,
  groupInfo,
}) => {
  const { t } = useTranslation('progressOverview');

  // Container reference for JavaScript positioning
  const containerRef = useRef<HTMLDivElement>(null);

  // Get viewport-based step spacing
  const stepSpacing = useStepSpacing();

  // Find the active step index from the backend data
  const activeStepIndex = findActiveStepIndex(steps);

  // Use active step scroll hook to center active step
  useActiveStepScroll(containerRef, activeStepIndex, steps.length, stepSpacing);

  // Enable drag-to-scroll for desktop users
  useDragScroll(containerRef);

  // Calculate progress percentage: only between active node and next node
  // Use currentUserProgress directly (which comes from progress-overview's debug system)
  const progressPercentage = useMemo(() => {
    return calculateProgressPercentage(
      steps,
      activeStepIndex,
      currentUserProgress,
    );
  }, [steps, activeStepIndex, currentUserProgress]);

  return (
    <Box sx={{ width: '100%', paddingY: 3 }}>
      <ProgressContainer ref={containerRef}>
        {/* Progress Lines - Using JavaScript positioning */}
        <JSProgressLines
          stepCount={steps.length}
          activeStepIndex={activeStepIndex}
          progressPercentage={progressPercentage}
          hasError={hasError}
          containerRef={containerRef}
        />

        {/* Steps with nodes and labels */}
        <StepsContainer stepCount={steps.length} stepSpacing={stepSpacing}>
          {steps.map((step, index) => {
            const isActive = step.active;
            const isCompleted = index <= activeStepIndex;
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;
            const isNextAfterActive =
              activeStepIndex >= 0 && index === activeStepIndex + 1;

            const nodeState: NodeState = {
              active: isActive,
              completed: isCompleted,
              category: step.category,
              isNextAfterActive,
              hasError: hasError,
            };

            return (
              <ProgressNode
                key={`${step._id}-${isCompleted}-${isActive}`}
                step={step}
                index={index}
                isFirst={isFirst}
                isLast={isLast}
                nodeState={nodeState}
                t={t}
                totalNodes={steps.length}
                stepSpacing={stepSpacing}
              />
            );
          })}
        </StepsContainer>
      </ProgressContainer>

      {/* Debug Panel - Only show when showDebug is true */}
      {showDebug && (
        <DebugPanel
          steps={steps}
          activeStepIndex={activeStepIndex}
          progressPercentage={progressPercentage}
          currentUserProgress={currentUserProgress}
          hasError={hasError}
          debug={{
            debugPNL: currentUserProgress,
            debugError: hasError,
            actualPNL: currentUserProgress,
            actualError: hasError,
            setDebugPNL: (_value: number) => {
              // No-op for internal debug panel
            },
            setDebugError: (_value: boolean) => {
              // No-op for internal debug panel
            },
          }}
          onDebugStepClick={onDebugStepClick}
          groupInfo={groupInfo}
        />
      )}
    </Box>
  );
};
